
import React from 'react';
import { cn } from '@/lib/utils';

interface ContentSectionProps {
  title?: string;
  children: React.ReactNode;
  height?: 'auto' | 'fixed' | 'flex';
  textSize?: 'sm' | 'md' | 'lg' | 'xl' | 'adaptive';
  className?: string;
  titleClassName?: string;
  variant?: 'default' | 'highlighted' | 'minimal';
}

export const ContentSection: React.FC<ContentSectionProps> = ({
  title,
  children,
  height = 'fixed',
  textSize = 'adaptive',
  className,
  titleClassName,
  variant = 'default'
}) => {
  const heightClasses = {
    auto: 'h-auto min-h-[12rem]',
    fixed: 'h-48 sm:h-56 md:h-64 lg:h-72 xl:h-80',
    flex: 'flex-1 min-h-[16rem]'
  };

  const textSizeClasses = {
    sm: 'text-base sm:text-lg md:text-xl',
    md: 'text-lg sm:text-xl md:text-2xl lg:text-3xl',
    lg: 'text-xl sm:text-2xl md:text-3xl lg:text-4xl',
    xl: 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl',
    adaptive: 'text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl'
  };

  const variantClasses = {
    default: 'rounded-xl border-2 border-white/25 bg-white/15 backdrop-blur-md',
    highlighted: 'rounded-xl border-2 border-white/35 bg-gradient-to-br from-white/25 to-white/10 backdrop-blur-lg shadow-lg',
    minimal: 'rounded-lg border border-white/20 bg-white/5 backdrop-blur-sm'
  };

  return (
    <div className={cn(
      "overflow-hidden flex flex-col transition-all duration-300 hover:shadow-lg",
      variantClasses[variant],
      heightClasses[height],
      className
    )}>
      {title && (
        <div className="relative border-b border-white/25 flex-shrink-0">
          {/* Soft fading gold border overlay for title */}
          <div className="absolute inset-0 rounded-t-xl bg-gradient-to-br from-yellow-400/30 via-transparent to-amber-500/30 pointer-events-none" />
          <div className="absolute inset-[1px] rounded-t-xl bg-gradient-to-br from-white/35 to-white/20 backdrop-blur-sm" />
          
          <div className="relative z-10 px-4 sm:px-6 py-3 sm:py-4">
            <h3 className={cn(
              "font-frank font-bold text-white text-shadow-md leading-tight",
              "text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl",
              titleClassName
            )}>
              {title}
            </h3>
          </div>
        </div>
      )}
      <div className={cn(
        "flex-1 p-4 sm:p-6 md:p-8 overflow-hidden flex flex-col justify-center",
        "font-heebo text-white text-shadow-sm leading-relaxed",
        textSizeClasses[textSize]
      )}>
        {children}
      </div>
    </div>
  );
};
